// ─────────────────────────────────────────────────────────────────────────────
// Auth client for the Service screen (Phase 7.5).
//
// This replaces Phase 7.3.1's authClient. The shape changes to track WHICH
// stage of the multi-step auth flow we're in:
//
//   stage: 'pending'  → user logged in but hasn't picked subsidiary yet
//   stage: 'subsidiary-chosen' → picked subsidiary but not branch yet
//   stage: 'session'  → fully signed in, ServiceScreen can render
//
// All three stages share the same localStorage key ('pos:service:auth').
// We discriminate by the `stage` field.
//
// Why ONE storage key for three stages: the user is in exactly one auth
// stage at any moment, and tracking them in one record means there's no
// "stale partial state" lurking in another key after they log out.
//
// Token lifetime:
//   - pending tokens: 5 min (server-enforced); lost on browser refresh by
//     design — see the planning doc, section 6.3.
//   - session tokens: 8 h (server-enforced); persisted across reloads.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'pos:service:auth';

export type AuthStage =
  | 'pending'              // after /login
  | 'subsidiary-chosen'    // after /select-subsidiary
  | 'session';             // after /select-branch

export interface AuthUser {
  id: number;
  username: string;
  name: string;
  name_ar: string | null;
  tenant_slug: string;
}

export interface AuthSubsidiary {
  id: number;
  code: string;
  name: string;
}

export interface AuthBranch {
  id: number;
  code: string;
  name: string;
  subsidiary_id: number;
  has_cash_drawer: boolean;
}

export interface AuthSessionInfo {
  user_id: number;
  username: string;
  tenant_db: string;
  tenant_slug: string;
  subsidiary_id: number;
  subsidiary_name: string;
  branch_id: number;
  branch_name: string;
  cash_drawer_id: number | null;
}

/**
 * Stored payload for stage='pending' (after /login but before subsidiary chosen).
 *
 * We persist the available subsidiaries here so a transient page refresh
 * doesn't force a re-login — but only within the 5-min pending TTL. After
 * that the next API call returns 401 and the auth store wipes storage.
 */
export interface PendingAuthData {
  stage: 'pending';
  token: string;
  saved_at: number;
  user: AuthUser;
  available_subsidiaries: AuthSubsidiary[];
}

export interface SubsidiaryChosenAuthData {
  stage: 'subsidiary-chosen';
  token: string;
  saved_at: number;
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  available_branches: AuthBranch[];
}

export interface SessionAuthData {
  stage: 'session';
  token: string;
  saved_at: number;
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  branch: AuthBranch;
  session: AuthSessionInfo;
}

export type AuthData =
  | PendingAuthData
  | SubsidiaryChosenAuthData
  | SessionAuthData;

// ── Public API ───────────────────────────────────────────────────────────────

export function saveAuth(data: AuthData): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function clearAuth(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}

/**
 * Read the stored auth payload, or null if none / malformed.
 *
 * We tolerate a missing storage key and unparseable JSON without throwing —
 * those just mean "no session yet", which is the same as "logged out".
 */
export function readAuth(): AuthData | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isAuthData(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Convenience: read just the bearer token, regardless of stage. */
export function getAuthToken(): string | null {
  const auth = readAuth();
  return auth?.token ?? null;
}

/** Convenience: are we fully signed in (stage === 'session')? */
export function isSessionReady(): boolean {
  const auth = readAuth();
  return auth?.stage === 'session';
}

// ── Type guards ──────────────────────────────────────────────────────────────

function isAuthData(value: unknown): value is AuthData {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.token !== 'string' || v.token.length === 0) return false;
  if (typeof v.saved_at !== 'number') return false;
  if (
    v.stage !== 'pending' &&
    v.stage !== 'subsidiary-chosen' &&
    v.stage !== 'session'
  ) {
    return false;
  }
  return true;
}
